import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { IconVariation } from 'components/charts/styled/DataCards.styled';
import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';

import PATHS, { mergePaths } from 'utils/paths';

import {
	faArrowRight,
	faCreditCard,
	faMoneyBillWave,
	faPlus,
	faReceipt,
	faWallet
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Credit.styled';

const Credit = ({ closePopper }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const { showModal } = useModalRouter();

	const { getLocaleDate } = useLocale();

	const { CODRemainingAmount, availableExtraCreditsList, availableFreeShipments, remainingAmount } = useSelector(
		(state) => state.user
	);

	const isEnterpriseUser = useSelector((state) => state.user.planName) === 'EnterprisePackage';

	useEffect(() => {
		fetchRemainingAmount().finally(() => setIsLoading(false));
	}, []);

	return (
		<S.Container>
			<S.Block>
				<S.InfoLineMain>
					<IconVariation
						color={'var(--black)'}
						backgroundcolor={'var(--topbar-popup-total-credit-bg)'}
						size="large"
						icon={faCreditCard}
					/>
					<S.InlineTypography>
						<Typography fontWeight="700" variant="p2">
							totalCredit
						</Typography>
						<Typography fontWeight="700" variant="h4" withCurrency>
							{remainingAmount}
						</Typography>
					</S.InlineTypography>
				</S.InfoLineMain>
				<S.InlineButtons>
					<Authorization path={`/payments/credit-transactions`}>
						<Button
							variant="outline"
							icon={faReceipt}
							label="transactions"
							size="medium"
							fullWidth
							onClick={() => {
								navigate(mergePaths(PATHS.PAYMENTS, PATHS.CREDIT_TRANSACTIONS));
								closePopper && closePopper();
							}}
						/>
					</Authorization>
					<Authorization path="/modal/charge-account-form">
						<Button
							variant="blue"
							icon={faPlus}
							fullWidth
							label="addCredit"
							size="medium"
							onClick={() => {
								showModal('charge-account-form');
								closePopper && closePopper();
							}}
							className="charge-account-button"
						/>
					</Authorization>
				</S.InlineButtons>
			</S.Block>
			{availableExtraCreditsList?.map((credit, idx) => (
				<S.Block key={`credit_${idx}`}>
					<S.InfoLineMain>
						<IconVariation
							color={'var(--topbar-popup-free-credit-icon)'}
							backgroundcolor={'var(--topbar-popup-free-credit-bg)'}
							size="large"
							icon={faMoneyBillWave}
						/>
						<S.InlineTypography>
							<div>
								<Typography fontWeight="700" variant="p2">
									freeCredits
								</Typography>
								<Typography color="var(--content-3)" variant="caption" fontWeight="500">
									{credit.promoCode && `(${credit.promoCode})`}
								</Typography>
							</div>
							<S.FlexContainer>
								<Typography fontWeight="700" variant="h4" withCurrency noWrap>
									{credit.remainingAmount}
								</Typography>
							</S.FlexContainer>
						</S.InlineTypography>
					</S.InfoLineMain>
					{credit?.expiryDate && (
						<S.InlineTypography>
							<Typography color="var(--content-disabled)" variant="caption">
								expiryDate
							</Typography>
							<Typography color="var(--content-disabled)" fontWeight="500" variant="caption">
								{getLocaleDate(credit?.expiryDate)}
							</Typography>
						</S.InlineTypography>
					)}
				</S.Block>
			))}
			{isLoading ? (
				<S.Block>
					<S.Loading>
						<Icon
							color={'var(--blue)'}
							size={'medium'}
							icon={faCircleNotch}
							className="button-icon fa-spin"
						/>
					</S.Loading>
				</S.Block>
			) : (
				availableFreeShipments?.map((freeShipment, idx) => (
					<S.Block key={`freeShipment_${idx}`}>
						<S.InfoLineMain>
							<IconVariation
								color={'var(--topbar-popup-used-free-shipment-icon)'}
								backgroundcolor={'var(--topbar-popup-used-free-shipment-bg)'}
								size="large"
								icon={faMoneyBillWave}
							/>
							<S.InlineTypography className="mb-3">
								<Typography fontWeight="700" noWrap variant="p2">
									{isEnterpriseUser ? t('usedFreeShipmentsEnterprise') : t('usedFreeShipments')}
								</Typography>
								<S.FlexContainer>
									{freeShipment?.remainingShipments !== undefined && freeShipment?.givenShipments ? (
										<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
											<Typography fontWeight="700" variant="h4" noWrap>
												{freeShipment?.givenShipments - freeShipment?.remainingShipments}
											</Typography>
											<Typography fontWeight="500" variant="p2" color="var(--content-3)" noWrap>
												/{freeShipment?.givenShipments}
											</Typography>
										</div>
									) : (
										<Typography fontWeight="700" noWrap>
											-
										</Typography>
									)}
								</S.FlexContainer>
							</S.InlineTypography>
						</S.InfoLineMain>

						{freeShipment?.expiryDate && (
							<S.InlineTypography>
								<Typography color="var(--content-disabled)" variant="caption">
									{isEnterpriseUser ? t('usageResetEnterprise') : t('usageReset')}
								</Typography>
								<Typography color="var(--content-disabled)" fontWeight="500" variant="caption">
									{getLocaleDate(freeShipment?.expiryDate)}
								</Typography>
							</S.InlineTypography>
						)}
					</S.Block>
				))
			)}
			<Authorization path={PATHS.COD_WALLET}>
				<S.Block>
					<S.InfoLineMain>
						<IconVariation
							color={'var(--topbar-popup-cod-wallet-icon)'}
							backgroundcolor={'var(--topbar-popup-cod-wallet-bg)'}
							size="large"
							icon={faWallet}
						/>
						<S.InlineTypography>
							<Typography fontWeight="700" variant="p2">
								CODWallet
							</Typography>
							<Typography fontWeight="700" variant="h4" withCurrency>
								{CODRemainingAmount ?? 0}
							</Typography>
						</S.InlineTypography>
					</S.InfoLineMain>
					<Button
						fullWidth
						variant="outline"
						label="codWalletDetails"
						icon={faArrowRight}
						iconPos="right"
						onClick={() => {
							navigate(PATHS.COD_WALLET);
							closePopper && closePopper();
						}}
					/>
				</S.Block>
			</Authorization>
		</S.Container>
	);
};
export default Credit;
